import { isNavigationFailure, NavigationFailureType } from "vue-router";

import type { Router } from "vue-router";
import { usePageMetaStore } from "@/store/pageMeta.store";
import { useAuthenticationStore } from "@/store/authentication.store";
import { buildOrgUrl } from "@/utils/navigation/orgUrl";

type NavigationDeps = {
  router: Router;
};

type OrganisationDetails = {
  id: string;
  name: string;
};

/** The store half of being scoped to a company; `remote/companyLanding.ts` needs it on its own. */
export const adoptMspOrganisation = async (
  organisation: OrganisationDetails,
): Promise<void> => {
  const authStore = useAuthenticationStore();
  try {
    await authStore.setMspUser({
      uuid: organisation.id,
      name: organisation.name,
    });
    await authStore.setCustomer(null);
  } catch {
    // handled centrally via axios interceptors
  }

  const pageMetaStore = usePageMetaStore();
  pageMetaStore.setMSP(organisation.name || "");
  pageMetaStore.setPageTitle({
    title: organisation.name || "",
  });
};

/** Counterpart of `adoptMspOrganisation`. */
export const adoptCustomerOrganisation = async (
  organisation: OrganisationDetails,
): Promise<void> => {
  const authStore = useAuthenticationStore();
  try {
    await authStore.setCustomer({
      uuid: organisation.id,
      name: organisation.name,
    });
  } catch {
    // handled centrally via axios interceptors
  }

  usePageMetaStore().setCustomerName(organisation.name || "");
};

/** A switch that quietly does nothing looks like one never asked for. A duplicate is routine. */
const reportFailedNavigation = (
  target: string,
  failure: unknown,
  thrown?: unknown,
): void => {
  if (thrown !== undefined) {
    // eslint-disable-next-line no-console
    console.error(
      `[titan] navigation to '${target}' threw — the app is still on the company it was on. ` +
        "A route guard rejected, most likely.",
      thrown,
    );
    return;
  }
  if (!failure) return;
  if (isNavigationFailure(failure, NavigationFailureType.duplicated)) return;
  // eslint-disable-next-line no-console
  console.warn(
    `[titan] navigation to '${target}' did not land — the app is still on the company it was ` +
      "on. Aborted or redirected by a route guard.",
    failure,
  );
};

export const navigateToMspOrganisation = async (
  { router }: NavigationDeps,
  organisation: OrganisationDetails,
) => {
  const orgId = organisation.id;
  if (!orgId) return;

  await adoptMspOrganisation(organisation);

  const target = { name: "msp-dashboard", params: { orgUuid: orgId } };
  try {
    reportFailedNavigation("msp-dashboard", await router.push(target));
  } catch (error) {
    reportFailedNavigation("msp-dashboard", null, error);
  }
};

/** Setting the store is load-bearing: CustomerLayout does not watch `route.params.orgUuid`. */
export const navigateToCustomerOrganisation = async (
  { router }: NavigationDeps,
  organisation: OrganisationDetails,
) => {
  const orgId = organisation.id;
  if (!orgId) return;

  await adoptCustomerOrganisation(organisation);

  const target = buildOrgUrl("customer", orgId, "");
  try {
    reportFailedNavigation(target, await router.push(target));
  } catch (error) {
    reportFailedNavigation(target, null, error);
  }
};
